import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { useAuth } from "../../../contexts/AuthContext";
import { useConvexAuth, useConvexNotifications } from "../../../services/convexService";
import { toast } from "sonner";
import { Home, LogOut, Bell, CheckCircle, XCircle, Clock, Mail, Globe, Settings } from "lucide-react";
import { Id } from "../../../../backend/_generated/dataModel";

const ProjectHolderNotifications = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { getUsersByRole } = useConvexAuth();

  // Update to use real notifications from Convex
  const {
    getAllNotifications,
    getUnreadNotifications,
    markAsRead,
    markAllAsRead: markAllAsReadMutation
  } = useConvexNotifications();

  interface Notification {
    _id: Id<"notifications">;
    userId: Id<"users">;
    title: string;
    message: string;
    type: "info" | "success" | "warning" | "error";
    read: boolean;
    createdAt: number;
    relatedId?: Id<"projects"> | Id<"investments">;
    relatedType?: string;
  }

  // Use the query hook directly instead of calling it inside useEffect
  const userNotifications = user ? getAllNotifications(user.id) : undefined;

  // Determine loading state based on whether the query result is available
  const loading = user && userNotifications === undefined;

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleMarkAsRead = async (notificationId: Id<"notifications">) => {
    if (!user) return;

    try {
      await markAsRead({ notificationId });

      // The UI will automatically update when the query re-fetches
      toast.success("Notification marked as read");
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      toast.error("Failed to mark notification as read");
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user) return;

    try {
      await markAllAsReadMutation({ userId: user.id });

      // The UI will automatically update when the query re-fetches
      toast.success("All notifications marked as read");
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
      toast.error("Failed to mark all notifications as read");
    }
  };

  const formatDate = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  };

  const unreadCount = userNotifications ? userNotifications.filter(n => !n.read).length : 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <img src="/logo.png" alt="Knowvest.plus Logo" className="w-6 h-6 object-contain" />
              </div>
              <h1 className="font-bold text-xl">Knowvest.plus</h1>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link to="/dashboard/project-holder">
                  <Home className="w-4 h-4 mr-2" />
                  Dashboard
                </Link>
              </Button>

              {user ? (
                <Button variant="outline" size="sm" onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              ) : (
                <Button variant="outline" size="sm" asChild>
                  <Link to="/login">Login</Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">الإشعارات</h1>
            <p className="text-muted-foreground">
              آخر التحديثات والتنبيهات المتعلقة بمشاريعك
            </p>
          </div>

          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">جميع الإشعارات</h2>
              {unreadCount > 0 && (
                <Badge variant="secondary">{unreadCount} غير مقروءة</Badge>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={handleMarkAllAsRead}>
              <CheckCircle className="w-4 h-4 mr-2" />
              تعليم الكل كمقروء
            </Button>
          </div>

          <div className="space-y-4">
            {loading ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : userNotifications && userNotifications.length > 0 ? (
              userNotifications.map((notification) => (
                <Card
                  key={notification._id}
                  className={`transition-all duration-200 ${!notification.read ? 'bg-muted/30 border-primary/30' : ''}`}
                >
                  <CardContent className="p-4 flex items-start gap-4">
                    <div className={`mt-1 w-3 h-3 rounded-full ${!notification.read ? 'bg-primary' : 'bg-transparent'}`}></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium">{notification.title}</h3>
                        <span className="text-xs text-muted-foreground">{formatDate(notification.createdAt)}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{notification.message}</p>
                      <div className="flex items-center gap-2 mt-3">
                        <Badge variant={
                          notification.type === 'success' ? 'default' :
                            notification.type === 'warning' ? 'destructive' :
                              notification.type === 'error' ? 'destructive' :
                                'secondary'
                        }>
                          {notification.type === 'success' && 'تم'}
                          {notification.type === 'info' && 'معلومة'}
                          {notification.type === 'warning' && 'تحذير'}
                          {notification.type === 'error' && 'خطأ'}
                        </Badge>
                      </div>
                    </div>
                    {!notification.read && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMarkAsRead(notification._id)}
                      >
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="text-center py-12">
                <Bell className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">لا توجد إشعارات</h3>
                <p className="text-muted-foreground">سيظهر هنا الإشعارات المتعلقة بمشاريعك</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProjectHolderNotifications;